import { useState, useCallback, useEffect, useRef } from 'react';
import type { ThemeMetadata } from '@/types/theme';

interface Props {
  themeIds: string[];
  metaMap: Record<string, ThemeMetadata>;
  selectedId: string | null;
  liveThemeId?: string;
  onSelect: (id: string) => void;
}

function CarouselCard({
  meta,
  role,
  isSelected,
  isLive,
  onClick,
}: {
  meta: ThemeMetadata;
  role: 'prev' | 'current' | 'next';
  isSelected?: boolean;
  isLive?: boolean;
  onClick?: () => void;
}) {
  const isCurrent = role === 'current';
  return (
    <div
      onClick={isCurrent ? onClick : undefined}
      className={`flex flex-col overflow-hidden border transition-all duration-300 select-none ${
        isCurrent
          ? 'cursor-pointer border-primary bg-surface-container shadow-2xl'
          : 'pointer-events-none border-border-subtle bg-surface-container-low'
      }`}
      style={{ borderRadius: 0 }}
    >
      {/* Thumbnail */}
      <div className="relative overflow-hidden" style={{ height: isCurrent ? 200 : 140 }}>
        {meta.thumbnail ? (
          <img
            src={meta.thumbnail}
            alt={meta.displayName}
            loading="lazy"
            className="w-full h-full object-cover"
            onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
          />
        ) : (
          <div
            className="w-full h-full"
            style={{ background: `linear-gradient(135deg, ${meta.accentColor ?? '#D4AF37'}33 0%, #0A0E1A 100%)` }}
          />
        )}
        {/* Accent stripe */}
        <div
          className="absolute bottom-0 left-0 right-0 h-0.5"
          style={{ backgroundColor: meta.accentColor ?? '#D4AF37' }}
        />
        {/* Badges */}
        <div className="absolute top-2 left-2 flex gap-1">
          {isLive && (
            <span className="px-1.5 py-0.5 font-label-caps text-[9px] uppercase bg-red-600 text-white">LIVE</span>
          )}
          {meta.featured && (
            <span className="px-1.5 py-0.5 font-label-caps text-[9px] uppercase bg-primary text-ink-black">Featured</span>
          )}
        </div>
      </div>

      {/* Info */}
      {isCurrent && (
        <div className="px-4 py-3 flex flex-col gap-1">
          <div className="flex items-center justify-between gap-2">
            <span className="font-headline-md text-[15px] text-on-surface truncate">
              {meta.displayName}
            </span>
            {isSelected && (
              <span className="flex-shrink-0 font-label-caps text-[9px] uppercase text-primary border border-primary px-2 py-0.5">
                Active
              </span>
            )}
          </div>
          <p className="font-meta-code text-[11px] text-meta-gray line-clamp-2">{meta.description}</p>
          <div className="flex items-center gap-2 mt-1">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: meta.accentColor ?? '#D4AF37' }} />
            <span className="font-meta-code text-[10px] text-meta-gray uppercase tracking-wider">{meta.family}</span>
            <span className="font-meta-code text-[10px] text-meta-gray">·</span>
            <span className="font-meta-code text-[10px] text-meta-gray capitalize">{meta.complexity}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ThemeCarouselHero({ themeIds, metaMap, selectedId, liveThemeId, onSelect }: Props) {
  const [currentIdx, setCurrentIdx] = useState(() => {
    if (!selectedId) return 0;
    const idx = themeIds.indexOf(selectedId);
    return idx >= 0 ? idx : 0;
  });
  const [flipping, setFlipping] = useState(false);
  const [flipDir, setFlipDir] = useState<'left' | 'right' | null>(null);
  const railRef = useRef<HTMLDivElement>(null);

  const total = themeIds.length;

  const navigate = useCallback(
    (dir: 'left' | 'right') => {
      if (flipping || total === 0) return;
      setFlipDir(dir);
      setFlipping(true);
      setTimeout(() => {
        setCurrentIdx((prev) =>
          dir === 'right' ? (prev + 1) % total : (prev - 1 + total) % total
        );
        setFlipping(false);
        setFlipDir(null);
      }, 300);
    },
    [flipping, total]
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') navigate('left');
      if (e.key === 'ArrowRight') navigate('right');
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  // Scroll thumbnail rail to keep current thumb visible
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const thumb = rail.querySelector<HTMLButtonElement>(`[data-idx="${currentIdx}"]`);
    if (thumb) thumb.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
  }, [currentIdx]);

  if (total === 0) return null;

  const prevIdx = (currentIdx - 1 + total) % total;
  const nextIdx = (currentIdx + 1) % total;

  const currentId = themeIds[currentIdx];
  const prevId = themeIds[prevIdx];
  const nextId = themeIds[nextIdx];

  // Dot indicators — show up to 12, centered on currentIdx
  const DOT_COUNT = Math.min(total, 12);
  const half = Math.floor(DOT_COUNT / 2);
  const dotStart = Math.max(0, Math.min(currentIdx - half, total - DOT_COUNT));
  const dotIds = Array.from({ length: DOT_COUNT }, (_, i) => dotStart + i);

  // 3D flip classes
  const centerTransform = flipping
    ? flipDir === 'right'
      ? 'rotateY(-90deg) scale(0.92)'
      : 'rotateY(90deg) scale(0.92)'
    : 'rotateY(0deg) scale(1)';

  return (
    <div className="w-full bg-ink-black border-b border-border-subtle pb-6">
      {/* ── 3-card carousel ── */}
      <div className="relative flex items-center justify-center gap-0 px-2 py-6" style={{ perspective: '1200px' }}>
        {/* Prev peeking */}
        <div
          className="w-[22%] opacity-40 flex-shrink-0 transition-all duration-300"
          style={{ transform: 'rotateY(22deg) translateX(12px) scale(0.9)', transformOrigin: 'right center' }}
        >
          <CarouselCard meta={metaMap[prevId]} role="prev" />
        </div>

        {/* Center card */}
        <div
          className="w-[52%] z-10 flex-shrink-0 transition-all duration-300"
          style={{ transform: centerTransform, transformOrigin: 'center center' }}
          onClick={() => {
            onSelect(currentId);
          }}
        >
          <CarouselCard
            meta={metaMap[currentId]}
            role="current"
            isSelected={selectedId === currentId}
            isLive={liveThemeId === currentId}
            onClick={() => onSelect(currentId)}
          />
        </div>

        {/* Next peeking */}
        <div
          className="w-[22%] opacity-40 flex-shrink-0 transition-all duration-300"
          style={{ transform: 'rotateY(-22deg) translateX(-12px) scale(0.9)', transformOrigin: 'left center' }}
        >
          <CarouselCard meta={metaMap[nextId]} role="next" />
        </div>

        {/* Arrow buttons */}
        <button
          onClick={() => navigate('left')}
          disabled={flipping}
          aria-label="Previous theme"
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center text-meta-gray hover:text-primary transition-colors disabled:opacity-30"
        >
          <span className="material-symbols-outlined text-[20px]">chevron_left</span>
        </button>
        <button
          onClick={() => navigate('right')}
          disabled={flipping}
          aria-label="Next theme"
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center text-meta-gray hover:text-primary transition-colors disabled:opacity-30"
        >
          <span className="material-symbols-outlined text-[20px]">chevron_right</span>
        </button>
      </div>

      {/* ── Dot indicators ── */}
      <div className="flex items-center justify-center gap-1.5 mb-4" aria-label="Theme position">
        {dotIds.map((idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIdx(idx)}
            aria-label={`Go to theme ${idx + 1}`}
            className={`rounded-full transition-all duration-200 ${
              idx === currentIdx
                ? 'w-4 h-1.5 bg-primary'
                : 'w-1.5 h-1.5 bg-border-subtle hover:bg-meta-gray'
            }`}
          />
        ))}
        {total > DOT_COUNT && (
          <span className="font-meta-code text-[9px] text-meta-gray ml-1 uppercase tracking-wider">
            {currentIdx + 1}/{total}
          </span>
        )}
      </div>

      {/* ── Thumbnail rail ── */}
      <div
        ref={railRef}
        className="flex gap-1.5 overflow-x-auto no-scrollbar px-4"
        style={{ scrollBehavior: 'smooth' }}
      >
        {themeIds.map((id, idx) => {
          const meta = metaMap[id];
          const isActive = idx === currentIdx;
          return (
            <button
              key={id}
              data-idx={idx}
              onClick={() => setCurrentIdx(idx)}
              title={meta.displayName}
              aria-label={meta.displayName}
              className={`flex-shrink-0 w-10 h-14 overflow-hidden border transition-all duration-200 ${
                isActive ? 'border-primary opacity-100' : 'border-border-subtle opacity-50 hover:opacity-80'
              }`}
              style={{ borderRadius: 0 }}
            >
              {meta.thumbnail ? (
                <img
                  src={meta.thumbnail}
                  alt={meta.displayName}
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
                />
              ) : (
                <div
                  className="w-full h-full"
                  style={{ background: `linear-gradient(135deg, ${meta.accentColor ?? '#D4AF37'}44 0%, #0A0E1A 100%)` }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
